import { Editor } from '@monaco-editor/react';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { renderHtmlWithTeX } from '../../../utils/RenderQuestionWithTex';
import Button from '../../Common/Button';
import Dropdown from '../../Common/Dropdown';
import TextField from '../../Common/TextField';

type IdealSolution = {
  language: string;
  answerCode: string;
  explanation: string;
};

type StarterCode = {
  language: string;
  code: string;
};

type McqOption = {
  answerOption: string;
};

const EditPopUp = ({ selectedQuestion, handleSaveEdit }: any) => {
  const [editedQuestion, setEditedQuestion] = useState(selectedQuestion);
  const [selectedLanguage, setSelectedLanguage] = useState<IdealSolution>();
  const [selectedStarterCode, setSelectedStarterCode] = useState<StarterCode>();
  const [isQuestionCovertion, setQuestionConvertion] = useState(false);

  const isEmpty = (value: any) => !value || !value.toString().trim();

  const isFormValid = () => {
    if (!editedQuestion) return false;

    if (!editedQuestion.questionText?.trim()) return false;

    if (editedQuestion.isProgrammingQuestion) {
      if (
        !Array.isArray(editedQuestion.idealSolutions) ||
        editedQuestion.idealSolutions.length === 0
      ) {
        return false;
      }

      const selectedSol = editedQuestion.idealSolutions.find(
        (sol: IdealSolution) => sol.language === selectedLanguage?.language
      );

      if (
        !selectedSol ||
        !selectedSol.answerCode?.trim() ||
        !selectedSol.explanation?.trim()
      ) {
        return false;
      }

      if (
        !Array.isArray(editedQuestion.starterCode) ||
        editedQuestion.starterCode.length === 0
      ) {
        return false;
      }

      const selectedStarter = editedQuestion.starterCode.find(
        (code: StarterCode) => code.language === selectedStarterCode?.language
      );

      if (!selectedStarter || !selectedStarter.code?.trim()) {
        return false;
      }

      if (
        !Array.isArray(editedQuestion.testCases) ||
        editedQuestion.testCases.length === 0
      ) {
        return false;
      }

      const allTestCasesValid = editedQuestion.testCases.every(
        (tc: any) => tc.input?.toString().trim() && tc.output?.toString().trim()
      );

      if (!allTestCasesValid) return false;
    } else {
      if (!editedQuestion.explanation?.trim()) return false;

      if (!editedQuestion.correctAnswer?.trim()) return false;

      if (
        !Array.isArray(editedQuestion.options) ||
        editedQuestion.options.length === 0
      ) {
        return false;
      }

      const allOptionsFilled = editedQuestion.options.every((opt: McqOption) =>
        opt.answerOption?.trim()
      );

      if (!allOptionsFilled) return false;
    }

    return true;
  };

  const handleTestCasesChange = (type: any, e: any, index: number) => {
    setEditedQuestion((prev: any) => {
      const testcase = [...prev.testCases];
      testcase[index][type] = e?.target?.value ?? '';

      return {
        ...prev,
        testCases: [...testcase]
      };
    });
  };

  const handleIdealSolutionChange = (e: any, type: string) => {
    setEditedQuestion((prev: any) => {
      if (!prev) return prev;

      const copy = [...prev.idealSolutions];
      const idx = copy.findIndex(
        each => each.language === selectedLanguage?.language
      );

      if (idx === -1) return prev;

      copy[idx] = {
        ...copy[idx],
        [type]: e
      };

      return {
        ...prev,
        idealSolutions: copy
      };
    });
  };

  const renderContent = () => {
    if (selectedQuestion?.isProgrammingQuestion) {
      return (
        <>
          <TextField
            isRequired
            label='Question'
            handleInputChange={e => {
              setEditedQuestion((prev: any) => {
                if (!prev) return prev;

                return {
                  ...prev,
                  questionText: e.target.value
                };
              });
            }}
            placeholder='Enter Question'
            type='text'
            value={editedQuestion?.questionText ?? ''}
          />

          <div>
            <button
              onClick={() => setQuestionConvertion(prev => !prev)}
              className='mt-2 flex w-full cursor-pointer justify-between rounded-[8px] border border-[#E3E5E9] bg-[#F5F9FF] px-3 py-2 font-semibold'
            >
              Question Convertion:
              <ChevronDown />
            </button>
            <p
              className={`${isQuestionCovertion ? 'block' : 'hidden'} px-3 py-2`}
            >
              {renderHtmlWithTeX(editedQuestion?.questionText, true)}
            </p>
          </div>

          <div className='flex w-full flex-col gap-3 md:flex-row'>
            <div className='md:w-1/2'>
              <div className='flex w-full flex-row items-center gap-2'>
                <p>Ideal Solutions:</p>

                <Dropdown
                  getOption={(language: any) => `${language.language}`}
                  handleDropdownChange={e => {
                    setSelectedLanguage(e);
                  }}
                  options={selectedQuestion?.idealSolutions}
                  value={selectedLanguage?.language ?? ''}
                  placeholder='Select a Language'
                  classes='mb-2 w-full'
                />
              </div>

              <Editor
                height='150px'
                language={selectedLanguage?.language?.toLowerCase()}
                value={
                  editedQuestion?.idealSolutions?.find(
                    (each: any) => each.language === selectedLanguage?.language
                  )?.answerCode ?? ''
                }
                theme='vs-dark'
                options={{
                  readOnly: !selectedLanguage?.language,
                  minimap: { enabled: false },
                  fontSize: 14
                }}
                onChange={e => handleIdealSolutionChange(e, 'answerCode')}
              />

              {selectedLanguage?.language && (
                <TextField
                  isRequired
                  label='Explanation'
                  handleInputChange={e =>
                    handleIdealSolutionChange(e?.target?.value, 'explanation')
                  }
                  placeholder='Enter Explanation'
                  type='text'
                  value={
                    editedQuestion?.idealSolutions?.find(
                      (each: any) =>
                        each.language === selectedLanguage?.language
                    )?.explanation ?? ''
                  }
                />
              )}
            </div>

            <div className='md:w-1/2'>
              <div className='flex flex-row items-center gap-3'>
                <p>Starter Code:</p>

                <Dropdown
                  getOption={(language: any) => `${language.language}`}
                  handleDropdownChange={e => {
                    setSelectedStarterCode(e);
                  }}
                  options={selectedQuestion?.starterCode}
                  value={selectedStarterCode?.language ?? ''}
                  placeholder='Select a Language'
                  classes='mb-2 w-full'
                />
              </div>

              <Editor
                height='150px'
                language={selectedStarterCode?.language?.toLowerCase()}
                value={
                  editedQuestion?.starterCode?.find(
                    (each: any) =>
                      each.language === selectedStarterCode?.language
                  )?.code ?? ''
                }
                theme='vs-dark'
                options={{
                  readOnly: !selectedStarterCode?.language,
                  minimap: { enabled: false },
                  fontSize: 14
                }}
                onChange={e => {
                  setEditedQuestion((prev: any) => {
                    if (!prev) return prev;

                    const copy = [...prev.starterCode];
                    const idx = copy.findIndex(
                      each => each.language === selectedStarterCode?.language
                    );

                    if (idx === -1) return prev;

                    copy[idx] = {
                      ...copy[idx],
                      code: e
                    };

                    return {
                      ...prev,
                      starterCode: copy
                    };
                  });
                }}
              />
            </div>
          </div>

          <div>
            <p className='my-2'>Test Cases:</p>

            <div className='flex flex-col gap-2 space-y-3 md:flex-row'>
              {selectedQuestion?.testCases?.map((each: any, index: number) => (
                <div
                  key={each?.input}
                  className='rounded-lg border border-gray-200 bg-gray-50 p-4'
                >
                  <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <div className='flex flex-col'>
                      <span className='text-sm font-semibold uppercase tracking-wide text-gray-600'>
                        Input:
                      </span>

                      <textarea
                        onChange={e => handleTestCasesChange('input', e, index)}
                        className='mt-2 overflow-x-auto rounded border border-gray-200 bg-white p-3 text-sm text-gray-800'
                      >
                        {each?.input}
                      </textarea>
                    </div>

                    <div className='flex flex-col'>
                      <span className='text-sm font-semibold uppercase tracking-wide text-gray-600'>
                        Output:
                      </span>

                      <textarea
                        onChange={e =>
                          handleTestCasesChange('output', e, index)
                        }
                        className='mt-2 overflow-x-auto rounded border border-gray-200 bg-white p-3 text-sm text-gray-800'
                      >
                        {each?.output}
                      </textarea>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <TextField
          isRequired
          label='Question'
          handleInputChange={e => {
            setEditedQuestion((prev: any) => {
              if (!prev) return prev;

              return {
                ...prev,
                questionText: e.target.value
              };
            });
          }}
          placeholder='Enter Question'
          type='text'
          value={editedQuestion?.questionText ?? ''}
        />

        <div>
          <button
            onClick={() => setQuestionConvertion(prev => !prev)}
            className='mt-2 flex w-full cursor-pointer justify-between rounded-[8px] border border-[#E3E5E9] bg-[#F5F9FF] px-3 py-2 font-semibold'
          >
            Question Convertion:
            <ChevronDown />
          </button>

          <p
            className={`${isQuestionCovertion ? 'block' : 'hidden'} px-3 py-2`}
          >
            {renderHtmlWithTeX(editedQuestion?.questionText, true)}
          </p>
        </div>

        <p className='my-2 text-sm font-bold text-gray-700'>Options:</p>

        {editedQuestion?.options?.map((each: any, index: number) => {
          return (
            <p key={each.answerOption || index} className='flex gap-2'>
              <TextField
                isRequired
                label={`${index}`}
                handleInputChange={e => {
                  setEditedQuestion((prev: any) => {
                    if (!prev) return prev;

                    const updatedOptions = [...prev.options];
                    updatedOptions[index].answerOption = e.target.value;

                    return {
                      ...prev,
                      options: updatedOptions
                    };
                  });
                }}
                placeholder='Enter Option'
                type='text'
                value={each?.answerOption ?? ''}
              />

              <p className='my-2 w-[50%] rounded-md border px-1'>
                {renderHtmlWithTeX(each?.answerOption, true)}
              </p>
            </p>
          );
        })}

        <div className='flex w-full gap-2'>
          <div>
            <TextField
              isRequired
              label='Correct Answer'
              handleInputChange={e => {
                setEditedQuestion((prev: any) => {
                  if (!prev) return prev;

                  return {
                    ...prev,
                    correctAnswer: e.target.value
                  };
                });
              }}
              placeholder='Enter Correct Answer'
              type='text'
              value={editedQuestion?.correctAnswer ?? ''}
            />
          </div>

          <p className='w-full rounded-md border px-1'>
            {renderHtmlWithTeX(editedQuestion?.correctAnswer, false)}
          </p>
        </div>

        <TextField
          isRequired
          label='Explanation'
          handleInputChange={e => {
            setEditedQuestion((prev: any) => {
              if (!prev) return prev;

              return {
                ...prev,
                explanation: e.target.value
              };
            });
          }}
          placeholder='Enter Explanation'
          type='text'
          value={editedQuestion?.explanation ?? ''}
        />

        <div>
          <button
            onClick={() => setQuestionConvertion(prev => !prev)}
            className='mt-2 flex w-full cursor-pointer justify-between rounded-[8px] border border-[#E3E5E9] bg-[#F5F9FF] px-3 py-2 font-semibold'
          >
            Explanation Convertion:
            <ChevronDown />
          </button>

          <p
            className={`${isQuestionCovertion ? 'block' : 'hidden'} px-3 py-2`}
          >
            {renderHtmlWithTeX(editedQuestion?.explanation, false)}
          </p>
        </div>
      </>
    );
  };

  return (
    <>
      {renderContent()}

      <div className='fixed bottom-0 right-0 flex w-full items-center justify-end bg-white px-6 py-2'>
        <Button
          label='Submit'
          variant='contained'
          handleClickBtn={() => {
            if (!isFormValid()) {
              toast.error('All fields are required');
              return;
            }

            handleSaveEdit(editedQuestion);
          }}
        />
      </div>
    </>
  );
};

export default EditPopUp;
